/* ============================================
   BRAIN BEAR - Three.js Gummy Bear Hero
   3D Exploding Gummy Bears Scene (OBJ Model)
   ============================================ */

import * as THREE from 'three';
import { OBJLoader } from 'three/addons/loaders/OBJLoader';

class GummyBearHero {
  constructor() {
    this.canvas = document.getElementById('gummy-bear-canvas');
    if (!this.canvas) return;

    this.container = this.canvas.parentElement;
    this.bears = [];
    this.mouse = new THREE.Vector2(0, 0);
    this.targetMouse = new THREE.Vector2(0, 0);
    this.clock = new THREE.Clock();
    this.isInView = true;
    this.animationPhase = 'exploding'; // 'exploding' | 'floating'
    this.explosionStart = 0;
    this.explosionDuration = 4.0;
    this.scrollY = 0;

    this.isMobile = window.innerWidth < 768;
    this.bearCount = this.isMobile ? 6 : 16;

    this.objUrl = this.canvas.dataset.objUrl;

    this.init();
    this.setupListeners();
    this.loadModel();
  }

  init() {
    // Scene
    this.scene = new THREE.Scene();

    // Camera
    this.camera = new THREE.PerspectiveCamera(
      60,
      this.container.clientWidth / this.container.clientHeight,
      0.1,
      1000
    );
    this.camera.position.z = 14;

    // Renderer
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
      alpha: true,
      powerPreference: 'high-performance'
    });
    this.renderer.setSize(this.container.clientWidth, this.container.clientHeight);
    this.renderer.setPixelRatio(this.isMobile ? Math.min(window.devicePixelRatio, 1.5) : Math.min(window.devicePixelRatio, 2));
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.2;

    // Lighting
    const ambientLight = new THREE.AmbientLight(0xFFF5E6, 0.7);
    this.scene.add(ambientLight);

    const keyLight = new THREE.DirectionalLight(0xFFFFFF, 1.2);
    keyLight.position.set(5, 8, 10);
    this.scene.add(keyLight);

    const fillLight = new THREE.PointLight(0xF5C542, 0.5, 50);
    fillLight.position.set(-6, 2, 8);
    this.scene.add(fillLight);

    const rimLight = new THREE.PointLight(0xE8873B, 0.4, 50);
    rimLight.position.set(0, -3, -5);
    this.scene.add(rimLight);

    const topLight = new THREE.DirectionalLight(0xFFF0D4, 0.3);
    topLight.position.set(0, 10, 0);
    this.scene.add(topLight);

    // Start render loop immediately (will just render empty scene until model loads)
    this.animate();
  }

  loadModel() {
    const loader = new OBJLoader();
    loader.load(this.objUrl, (obj) => {
      // Merge all child geometries into one BufferGeometry
      let mergedGeometry = null;
      obj.traverse((child) => {
        if (child.isMesh) {
          const geom = child.geometry.clone();
          geom.applyMatrix4(child.matrixWorld);
          if (!mergedGeometry) {
            mergedGeometry = geom;
          }
        }
      });

      if (!mergedGeometry) return;

      // Center the geometry
      mergedGeometry.computeBoundingBox();
      mergedGeometry.center();

      // Store the template geometry for cloning
      this.bearGeometry = mergedGeometry;
      this.createBears();
    });
  }

  createBears() {
    // Blood orange color variations — deep, rich dark orange-red
    const colorVariations = [
      0xCC4E00, // Blood orange (primary)
      0xB34200, // Deeper blood orange
      0xD45500, // Slightly warmer
      0xA03800, // Dark burnt orange
      0xC04800, // Rich dark orange
    ];

    // The OBJ model is ~6.5 units tall; scale to ~1.5 units like the original procedural bears
    const baseModelScale = 1.5 / 6.5;

    for (let i = 0; i < this.bearCount; i++) {
      // Create a mesh from the loaded geometry
      const colorIndex = i % colorVariations.length;
      const color = new THREE.Color(colorVariations[colorIndex]);

      const material = new THREE.MeshStandardMaterial({
        color: color,
        roughness: 0.22,
        metalness: 0.05,
        transparent: true,
        opacity: 0.93,
        side: THREE.FrontSide,
        envMapIntensity: 0.8
      });

      const bear = new THREE.Mesh(this.bearGeometry, material);

      // Random scale variation
      const scaleVar = 0.45 + Math.random() * 0.45;
      const s = baseModelScale * scaleVar;
      bear.scale.set(s, s, s);

      // Start at center (for explosion)
      bear.position.set(0, 0, 0);

      // Golden-angle spiral for uniform distribution with slight jitter
      const goldenAngle = Math.PI * (3 - Math.sqrt(5));
      const angle = i * goldenAngle;
      const radius = Math.sqrt((i + 0.5) / this.bearCount) * 8;
      const jitterX = (Math.random() - 0.5) * 1.0;
      const jitterY = (Math.random() - 0.5) * 0.8;
      const jitterZ = (Math.random() - 0.5) * 1.0;

      bear.userData = {
        targetPos: new THREE.Vector3(
          Math.cos(angle) * radius + jitterX,
          Math.sin(angle) * radius * 0.65 + jitterY,
          (Math.random() - 0.5) * 6 + 2 + jitterZ
        ),
        floatSpeed: 0.15 + Math.random() * 0.1,
        floatAmplitude: 0.1 + Math.random() * 0.1,
        floatOffset: Math.random() * Math.PI * 2,
        rotSpeed: new THREE.Vector3(
          (Math.random() - 0.5) * 0.15,
          (Math.random() - 0.5) * 0.15,
          (Math.random() - 0.5) * 0.08
        ),
        initialRot: new THREE.Vector3(
          Math.random() * Math.PI * 2,
          Math.random() * Math.PI * 2,
          Math.random() * Math.PI * 2
        ),
        explosionDir: new THREE.Vector3(
          (Math.random() - 0.5),
          (Math.random() - 0.5),
          Math.random() * 0.5 + 0.3
        ).normalize(),
        explosionDelay: Math.random() * 0.6,
        parallaxStrength: 0.3 + Math.random() * 0.3,
        basePos: new THREE.Vector3(),
        // Store the intended scale for the explosion scale-up
        targetScale: s
      };

      bear.rotation.set(
        bear.userData.initialRot.x,
        bear.userData.initialRot.y,
        bear.userData.initialRot.z
      );

      // Start invisible for explosion reveal
      bear.scale.set(0, 0, 0);

      this.scene.add(bear);
      this.bears.push(bear);
    }

    this.explosionStart = this.clock.getElapsedTime();
  }

  setupListeners() {
    if (!this.isMobile) {
      window.addEventListener('mousemove', (e) => {
        this.targetMouse.x = (e.clientX / window.innerWidth) * 2 - 1;
        this.targetMouse.y = -(e.clientY / window.innerHeight) * 2 + 1;
      });
    }

    window.addEventListener('scroll', () => {
      this.scrollY = window.scrollY;
    }, { passive: true });

    window.addEventListener('resize', () => this.onResize());

    const observer = new IntersectionObserver(
      ([entry]) => {
        this.isInView = entry.isIntersecting;
      },
      { threshold: 0 }
    );
    observer.observe(this.container);
  }

  onResize() {
    const w = this.container.clientWidth;
    const h = this.container.clientHeight;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h);
    this.isMobile = window.innerWidth < 768;
  }

  easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  animate() {
    requestAnimationFrame(() => this.animate());

    if (!this.isInView) return;

    const elapsed = this.clock.getElapsedTime();
    const explosionElapsed = elapsed - this.explosionStart;

    // Smooth mouse interpolation
    this.mouse.lerp(this.targetMouse, 0.05);

    // Scroll-based effects
    const heroHeight = this.container.clientHeight || window.innerHeight;
    const scrollProgress = Math.min(this.scrollY / heroHeight, 1);
    const scrollFade = 1 - scrollProgress;
    const scrollSpread = 1 + scrollProgress * 2;

    // Update bears
    this.bears.forEach((bear) => {
      const data = bear.userData;
      const delay = data.explosionDelay;
      const adjustedTime = Math.max(0, explosionElapsed - delay);

      if (this.animationPhase === 'exploding' && adjustedTime < this.explosionDuration) {
        // --- EXPLOSION PHASE ---
        const t = adjustedTime / this.explosionDuration;
        const easedT = this.easeOutCubic(Math.min(t * 1.1, 1));

        // Move from center to target position
        bear.position.lerpVectors(
          new THREE.Vector3(0, 0, 0),
          data.targetPos,
          easedT
        );

        // Scale up from 0
        const scaleT = this.easeOutCubic(Math.min(t * 1.5, 1));
        const s = scaleT * data.targetScale;
        bear.scale.set(s, s, s);

        // Gentle spin during explosion
        bear.rotation.x += data.rotSpeed.x * 0.8 * (1 - t);
        bear.rotation.y += data.rotSpeed.y * 0.8 * (1 - t);
        bear.rotation.z += data.rotSpeed.z * 0.8 * (1 - t);

        // Save base position once settled
        if (t > 0.95) {
          data.basePos.copy(data.targetPos);
        }
      } else {
        // --- FLOATING PHASE ---
        if (this.animationPhase === 'exploding') {
          this.animationPhase = 'floating';
          this.bears.forEach(b => {
            b.userData.basePos.copy(b.userData.targetPos);
          });
        }

        const floatTime = elapsed;

        const floatX = Math.sin(floatTime * data.floatSpeed + data.floatOffset) * data.floatAmplitude;
        const floatY = Math.cos(floatTime * data.floatSpeed * 0.7 + data.floatOffset + 1.5) * data.floatAmplitude * 0.8;
        const floatZ = Math.sin(floatTime * data.floatSpeed * 0.5 + data.floatOffset + 3.0) * data.floatAmplitude * 0.3;

        const parallaxX = this.mouse.x * data.parallaxStrength * 0.5;
        const parallaxY = this.mouse.y * data.parallaxStrength * 0.3;

        bear.position.x = (data.basePos.x + floatX + parallaxX) * scrollSpread;
        bear.position.y = (data.basePos.y + floatY + parallaxY) * scrollSpread;
        bear.position.z = data.basePos.z + floatZ;

        bear.rotation.x += data.rotSpeed.x * 0.004;
        bear.rotation.y += data.rotSpeed.y * 0.004;
        bear.rotation.z += data.rotSpeed.z * 0.002;
      }

      // Apply scroll fade
      bear.material.opacity = 0.93 * scrollFade;
    });

    this.renderer.render(this.scene, this.camera);
  }
}

// Initialize when DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new GummyBearHero());
} else {
  new GummyBearHero();
}
